import animationData from "@/animation.json";
import { CustomDialogBox } from "@/components/Dialog";
import ProjectDropdown from "@/components/ProjectDropdown";
import ToggleButton from "@/components/ToggleButton";
import Alert from "@/components/topbar/Alert";
import { ACCEPTED, ASSIGNED, AVAILABLE } from "@/constants/area";
import { LookupValueMapping } from "@/constants/lookups";
import { RELEASE_PENDING, RELEASED } from "@/constants/request";
import {
  ENGINEER,
  PROJECT_ADMIN,
  SUBCONTRACTOR,
  SUPER_ADMIN,
} from "@/constants/roles";
import { OVERDUE, REJECTED } from "@/constants/tasks";
import { useCompaniesWithUsers } from "@/hooks/useCompanies";
import {
  useAllLevels,
  useAreasByLevel,
  useHistoryByLevel,
  useUpdateLevel,
} from "@/hooks/useLevels";
import { useAllLookups } from "@/hooks/useLookups";
import useNotificationHandler from "@/hooks/useNotificationHandler";
import { useAllProjects } from "@/hooks/useProjects";
import { ICompanyWithUsers } from "@/interfaces/company.interface";
import { IArea, IAssignedUser, ILevel } from "@/interfaces/level.interface";
import { ILookup } from "@/interfaces/lookups.interface";
import {
  ICompanyUser,
  IRequest
} from "@/interfaces/request.interface";
import { ITaskAction } from "@/interfaces/task.interface";
import { useAppSelector } from "@/store/hooks/hooks";
import { hideLoader, showLoader } from "@/store/slices/loaderSlice";
import { RootState } from "@/store/store";
import {
  clearLocalStorageExcept,
  isAuthorize,
  isTouchScreen,
} from "@/utils/common";
import { levelFilters } from "@/utils/kendo";
import ToastService from "@/utils/toast";
import { useMsal } from "@azure/msal-react";
import { Controls, Player } from "@lottiefiles/react-lottie-player";
import { Dialog } from "@progress/kendo-react-dialogs";
import {
  ComboBox,
  ComboBoxChangeEvent,
  ListItemProps
} from "@progress/kendo-react-dropdowns";
import { Avatar } from "@progress/kendo-react-layout";
import { Popup } from "@progress/kendo-react-popup";
import {
  AsteriskSquare,
  Badge,
  BookCheck,
  Hexagon,
  LogOut,
  Octagon,
  RefreshCcw,
  Save,
  Settings,
  Square,
  Trash2
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AssignmentsDrawer from "./components/AssignmentsDrawer";
import CanvasEditor from "./components/CanvasEditor";
import DetailsDrawer from "./components/DetailsDrawer";
import NameDialog from "./components/NameDialog";
import SubmissionDrawer from "./components/SubmissionDrawer";
import TaskActionDrawer from "./components/TaskActionDrawer";
function Home() {
  // Activities filter state
  const [selectedCompany, setSelectedCompany] =
    useState<ICompanyWithUsers | null>(null);
  // Use the same structure as ICompanyWithUsers.users for selectedSubcon
  const [selectedSubcon, setSelectedSubcon] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [activitiesOptions, setActivitiesOptions] = useState<ILookup[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<ILookup | null>(
    null
  );
  // Fetch all activities from lookups
  const { data: activitiesData } = useAllLookups(
    {
      filter: {
        logic: "and",
        filters: [
          {
            field: "type",
            operator: "eq",
            value: LookupValueMapping.Activities,
          },
        ],
      },
    },
    { enabled: true }
  );

  // Assuming you have a hook or function to fetch all projects
  const { data: projects } = useAllProjects();

  const { selectedProject } = useAppSelector(
    (state: RootState) => state.projects
  );

  const { data: companies, refetch: refecthCompanies } = useCompaniesWithUsers(
    selectedProject?.id!,
    {},
    {
      enabled: !!selectedProject?.id,
    }
  );
  const isFilterSet = useRef(false);
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.user);
  const maxW = "75vw";
  const selectedAreaId = useRef<string | null>(null);
  const [imagePreviewWidth, setImagePreviewWidth] = useState("90vw");
  const [imagePreviewHeight, setImagePreviewHeight] = useState("50vh");
  const [assignedToMe, setAssignedToMe] = useState<boolean>(false);
  const [selectedRequest, setSelectedRequest] = useState<IRequest | null>(null);
  // Calculate and set drawer width based on orientation
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isLandscape = width > height;

      // Use different widths based on device size and orientation
      if (width <= 768 || width <= 1024 || isTouchScreen()) {
        // Mobile
        setImagePreviewWidth("100vw");
        setImagePreviewHeight(height + "px");
      } else {
        // Desktop
        setImagePreviewWidth(maxW);
      }
    };

    // Initialize
    handleResize();

    // Add event listeners
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [maxW]);

  // Update subcons and activities options when company changes
  useEffect(() => {
    if (selectedCompany === null && companies?.data) {
      // Restore all subcons from all companies when company filter is cleared
      const allCompanyUsers = companies.data.flatMap((company) =>
        (company.users || []).map((user) => ({
          ...user,
          name: `${company.name} - ${user.name}`,
        }))
      );
      const unique = Array.from(
        new Map(allCompanyUsers.map((u) => [u.id, u])).values()
      ).sort((a, b) => a.name.localeCompare(b.name));
      setAssignedSubcon(unique);
      // Show all activities when no company is selected, filtering out empty values
      const allActivities = (activitiesData?.data || []).filter(
        (a: ILookup) =>
          a.value &&
          a.value.trim() !== "" &&
          a.projectId === selectedProject?.id &&
          a.isActive === true
      );
      setActivitiesOptions(allActivities);
      setSelectedSubcon(null);
      setSelectedActivity(null);
      setLocalAreas(fetchedAreas);
      return;
    }
    if (selectedCompany && companies?.data && activitiesData?.data) {
      // Filter subcons for selected company and concat company name
      const subconsWithCompany = (selectedCompany.users || []).map((user) => ({
        ...user,
        name: `${selectedCompany.name} - ${user.name}`,
      }));
      setAssignedSubcon(subconsWithCompany);
      // Filter activities from lookups using company activity ids
      if (Array.isArray(selectedCompany.activities)) {
        setActivitiesOptions(
          activitiesData.data.filter(
            (a: ILookup) =>
              typeof a.id === "string" &&
              selectedCompany.activities.includes(a.id) &&
              a.value &&
              a.value.trim() !== "" &&
              a.projectId === selectedProject?.id &&
              a.isActive === true
          )
        );
      } else {
        setActivitiesOptions([]);
      }
      // Filter areas by assigned companies
      if (areasData?.data) {
        const filteredAreas = areasData.data.filter((area: any) =>
          area.assignedCompanies?.some(
            (c: any) => c.companyId === selectedCompany.id
          )
        );
        setLocalAreas(filteredAreas);
        //setFetchedAreas(filteredAreas);
      }
      // Only reset selectedSubcon if not already set or not in new list
      if (
        !selectedSubcon ||
        !subconsWithCompany.some((u) => u.id === selectedSubcon.id)
      ) {
        setSelectedSubcon(null);
      }
      setSelectedActivity(null);
    } else {
      setAssignedSubcon([]);
      setActivitiesOptions([]);
      setSelectedSubcon(null);
      setSelectedActivity(null);
      setLocalAreas(fetchedAreas);
      //setFetchedAreas([]);
    }
  }, [selectedCompany, companies, activitiesData]);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [imageSrc, setImageSrc] = useState("");

  const [isViewMode, setIsViewMode] = useState(false);

  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);

  const [selectedUsersFilter, setSelectedUsersFilter] = useState<string | null>(
    ""
  );
  const [selectedAreaStatus, setSelectedAreaStatus] = useState<string | null>(
    ""
  );
  const [selectedAarStatus, setSelectedAarStatus] = useState<string | null>("");

  // 1) fetched vs local
  const [fetchedAreas, setFetchedAreas] = useState<IArea[]>([]);
  const [localAreas, setLocalAreas] = useState<IArea[]>([]);

  const [isAreasModified, setIsAreasModified] = useState(false);
  const [currentLevel, setCurrentLevel] = useState<ILevel | null>(null);
  const [currentLevelVersion, setCurrentLevelVersion] = useState<ILevel | null>(
    null
  );
  //
  const [imageUrl, setImageUrl] = useState<string>("");
  const [shapeType, setShapeType] = useState<string>("");
  const [isDrawMode, setIsDrawMode] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  const [drawLabels, setDrawLabels] = useState(false);
  const [currentAreaName, setCurrentAreaName] = useState<string>("");
  const [showSubmissionDrawer, setShowSubmissionDrawer] =
    useState<boolean>(false);
  const [showTaskActionDrawer, setShowTaskActionDrawer] =
    useState<boolean>(false);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState<boolean>(false);
  const [showAssignmentsDrawer, setShowAssignmentsDrawer] =
    useState<boolean>(false);
  const [taskAction, setTaskAction] = useState<ITaskAction | null>(null);
  const [assignedSubcon, setAssignedSubcon] = useState<ICompanyUser[]>([]);

  const [levelHistory, setLevelHistory] = useState<ILevel[]>([]);
  const [showLoading, setShowLoading] = useState(true);
  // Save selected level to localStorage whenever it changes
  useEffect(() => {
    if (selectedLevel) {
      localStorage.setItem("selectedLevel", selectedLevel);
    }
  }, [selectedLevel]);

  // Save selected users filter to localStorage whenever it changes
  useEffect(() => {
    if (selectedUsersFilter !== undefined) {
      localStorage.setItem("selectedUsersFilter", selectedUsersFilter!);
    }
  }, [selectedUsersFilter]);

  // Save selected area status to localStorage whenever it changes
  useEffect(() => {
    if (selectedAreaStatus !== undefined) {
      localStorage.setItem("selectedAreaStatus", selectedAreaStatus || "");
    }
  }, [selectedAreaStatus]);

  // Save selected AAR status to localStorage whenever it changes
  useEffect(() => {
    if (selectedAarStatus !== undefined) {
      localStorage.setItem("selectedAarStatus", selectedAarStatus || "");
    }
  }, [selectedAarStatus]);

  const { data: levelsData, refetch: refetchAllLevels, isLoading: isLoadingLevels, isFetching: isFetchingLevels } = useAllLevels(
    {
      filter: levelFilters(
        isAuthorize([PROJECT_ADMIN]),
        selectedProject?.id,
        null,
        false
      ),
    },
    {
      enabled: !!selectedProject?.id && !!user,
      showLoader: true,
    }
  );

  function handlePreview(base64: string, name: string) {
    if (base64) setShowImageDialog(!showImageDialog);
    setImageSrc(base64);
  }

  const { data: levelHistoryData, refetch: refetchHistory } = useHistoryByLevel(
    selectedLevel,
    {
      enabled: isAuthorize([SUPER_ADMIN, PROJECT_ADMIN]) && !!selectedLevel,
      showLoader: showLoading,
    }
  );

  const { data: areasData, isLoading: isLoadingAreas, isFetching: isFetchingAreas, refetch: refetchAreas } = useAreasByLevel(
    currentLevelVersion?.id,
    currentLevelVersion?.version,
    {
      enabled: !!selectedLevel && !!currentLevelVersion,
      showLoader: showLoading,
    }
  );

  const popupRef = useRef(null);
  const [show, setShow] = React.useState(false);

  // Add this useEffect to handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        show &&
        popupRef.current &&
        anchor.current &&
        !anchor.current.contains(event.target) &&
        !(popupRef.current as any).contains(event.target)
      ) {
        setShow(false);
      }
    };

    // Add event listener when popup is shown
    if (show) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Clean up the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [show]);

  // Toggle function for the button
  const togglePopup = () => {
    setShow(!show);
  };

  useEffect(() => {
    if (levelHistoryData?.data && currentLevel) {
      if (
        !levelHistoryData?.data.some(
          (item) =>
            item.parentLevelId === currentLevel?.id &&
            item.version === currentLevel?.version
        )
      ) {
        levelHistoryData?.data.push({
          ...currentLevel!,
          parentLevelId: currentLevel?.id,
        });
      }

      setLevelHistory(levelHistoryData?.data);
    }
  }, [levelHistoryData, currentLevel]);

  const updateLevelMutation = useUpdateLevel({ showLoader: showLoading });

  const { handleNotificationClick } = useNotificationHandler(
    setSelectedLevel,
    setShowAssignmentsDrawer,
    selectedAreaId
  );

  const handleAction = (data: ITaskAction) => {
    setTaskAction(data);
    setShowDetailsDrawer(false);
    setShowTaskActionDrawer(true);
  };

  // 2) sync server → local exactly once per load
  useEffect(() => {
    if (areasData?.data && companies?.data) {
      setFetchedAreas(areasData.data);
      setLocalAreas(areasData.data);

      // Get all users from all companies, format as 'CompanyName - Username', dedupe by userId
      const allCompanyUsers = companies.data.flatMap((company) =>
        (company.users || []).map((user) => ({
          ...user,
          name: `${company.name} - ${user.name}`,
        }))
      );
      const unique = Array.from(
        new Map(allCompanyUsers.map((u) => [u.id, u])).values()
      ).sort((a, b) => a.name.localeCompare(b.name));
      setAssignedSubcon(unique);
      isFilterSet.current = true;
    }
  }, [areasData?.data]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const areaId = params.get("areaId");
    const levelId = params.get("levelId");
    const projectId = params.get("projectId");

    if (areaId && levelId && projectId) {
      handleNotificationClick({ areaId, levelId, projectId });
      window.history.replaceState({}, document.title, "/home");
    }
  }, [location]);

  useEffect(() => {
    const handleOpenDetailsDrawer = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { areaId, levelId } = customEvent.detail;

      setSelectedLevel(levelId);
      selectedAreaId.current = areaId;
      setShowAssignmentsDrawer(true);
    };

    window.addEventListener("open-details-drawer", handleOpenDetailsDrawer);

    return () => {
      window.removeEventListener(
        "open-details-drawer",
        handleOpenDetailsDrawer
      );
    };
  }, []);

  function filterLevels(levels: ILevel[]): ILevel[] {
    const groupedLevels: Record<string, ILevel[]> = {};

    // Group levels by name
    levels.forEach((level) => {
      if (!groupedLevels[level.name]) {
        groupedLevels[level.name] = [];
      }
      groupedLevels[level.name].push(level);
    });

    // Filter out published levels if a draft exists
    return levels
      .filter((level) => {
        const sameNameLevels = groupedLevels[level.name];
        const hasDraft = sameNameLevels.some((l) => l.status === "Draft");
        return !(hasDraft && level.status === "Published");
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  useEffect(() => {
    if (!user) return;
    const levels = levelsData?.data as unknown as ILevel[];
    if (levels && levels.length > 0) {
      if (!selectedLevel) {
        const savedLevel = localStorage.getItem("selectedLevel");
        if (savedLevel) {
          setSelectedLevel(savedLevel);
        } else {
          setSelectedLevel(levels[0].id!);
        }
      }

      const level = levels.find((level) => level.id === selectedLevel);
      if (level) {
        if (level.imageUrl) {
          setImageUrl(level.imageUrl);
        }
        setCurrentLevel(level);
        const draftLevelHistory = levelHistory?.find(
          (x) => x.parentLevelId === level.id && x.status === "Draft"
        );

        if (draftLevelHistory) {
          setCurrentLevelVersion(draftLevelHistory);
        } else {
          const publishedLevelHistory = levelHistory?.find(
            (x) => x.parentLevelId === level.id && x.status === "Published"
          );

          if (publishedLevelHistory) {
            setCurrentLevelVersion(publishedLevelHistory);
          } else {
            setCurrentLevelVersion(level);
          }
        }
      }
    } else {
      setCurrentLevel(null);
      setCurrentLevelVersion(null);
      setFetchedAreas([]);
      setImageUrl("");
    }
  }, [user, levelsData, selectedLevel, levelHistory]);

  useEffect(() => {
    if (selectedProject) {
      setSelectedLevel("");
      setCurrentLevel(null);
      setCurrentLevelVersion(null);
      setImageUrl("");
      refecthCompanies().then(() => {
        isFilterSet.current = false;

        // Only restore filters after user is authenticated
        const savedLevel = localStorage.getItem("selectedLevel");
        const savedUsers = localStorage.getItem("selectedUsersFilter");
        const savedAreaStatus = localStorage.getItem("selectedAreaStatus");
        const savedAarStatus = localStorage.getItem("selectedAarStatus");
        if (savedLevel) setSelectedLevel(savedLevel);
        if (savedUsers) setSelectedUsersFilter(savedUsers);
        if (savedAreaStatus) setSelectedAreaStatus(savedAreaStatus);
        if (savedAarStatus) setSelectedAarStatus(savedAarStatus);
      });
    }
  }, [selectedProject]);

  // 3) refetch only on level change
  const versionId = currentLevelVersion?.id;
  useEffect(() => {
    if (versionId) {
      refetchAreas();
    }
  }, [versionId]);

  useEffect(() => {
    if (!showDialog) {
      selectedAreaId.current = null;
    }
  }, [showDialog]);

  const handleSaveLevel = (action: string) => {
    // Check if any area is missing a name
    const unnamedArea = localAreas.find((area) => !area.name);

    if (unnamedArea) {
      ToastService.error("Each area must have a name");
      // Prompt for naming the unnamed area
      handleAreaClick(unnamedArea.id || "");
      return;
    }

    if (currentLevel && selectedLevel) {
      const updatedLevelData = {
        ...currentLevel,
        areas: localAreas.map((area) => ({
          ...area,
          id: area.id?.includes("new") ? "" : area.id,
        })),
        action: action,
      };
      setShowLoading(false);
      dispatch(showLoader());

      updateLevelMutation.mutate(
        {
          id: selectedLevel,
          data: updatedLevelData,
        },
        {
          onSuccess: () => {
            setIsAreasModified(false);
            ToastService.success("Level saved successfully");
            // Use Promise.all to ensure all refetches complete
            Promise.all([refetchAllLevels(), refetchHistory(), refetchAreas()])
              .then(() => {
                dispatch(hideLoader());
                setShowLoading(true);
              })
              .catch(() => {
                // Important: Handle errors to ensure loader is hidden
                dispatch(hideLoader());
                setShowLoading(true);
              });
          },
          onError: () => {
            // Important: Handle errors to ensure loader is hidden
            dispatch(hideLoader());
            setShowLoading(true);
          },
        }
      );
    }
  };

  const handleLevelChange = (levelId: string) => {
    if (isAreasModified) {
      if (
        window.confirm(
          "You have unsaved changes. Do you want to save them before switching levels?"
        )
      ) {
        handleSaveLevel("draft");
      }
    }
    isFilterSet.current = false;
    setLevelHistory([]);
    setSelectedLevel(levelId);
    localStorage.setItem("selectedLevel", levelId);
    refetchHistory();
    setIsAreasModified(false);
  };

  const handleLevelHistoryChange = (level: ILevel) => {
    if (isDrawMode) {
      setCanEdit(false);
      setIsDrawMode(false);
    }

    if (!level) return;
    if (isAreasModified) {
      if (
        window.confirm(
          "You have unsaved changes. Do you want to save them before changing level version?"
        )
      ) {
        if (
          currentLevel?.id === level.parentLevelId &&
          currentLevel?.version !== level.version
        ) {
          setReadOnlyMode(true);
          //refetchAreas();
        } else setReadOnlyMode(false);

        setIsAreasModified(false);
      }
    } else {
      if (
        currentLevel?.id === level.parentLevelId &&
        currentLevel?.version !== level.version
      ) {
        setReadOnlyMode(true);
        //refetchAreas();
      } else setReadOnlyMode(false);
    }
    setCurrentLevelVersion(level);
  };

  const userFilterChange = (e: ComboBoxChangeEvent) => {
    setSelectedSubcon(e.value);
  };

  const areaStatusChange = (e: ComboBoxChangeEvent) => {
    const statusValue = e.value?.value ?? "";
    setSelectedAreaStatus(statusValue);
    localStorage.setItem("selectedAreaStatus", statusValue);
  };

  const aarStatusChange = (e: ComboBoxChangeEvent) => {
    const statusValue = e.value?.value ?? "";
    setSelectedAarStatus(statusValue);
    localStorage.setItem("selectedAarStatus", statusValue);
  };

  const handleSaveName = (name: string) => {
    if (selectedAreaId.current) {
      // add unique name check

      const isNameExists = localAreas.some(
        (area) =>
          area.name?.toLowerCase() === name.toLowerCase() &&
          area.id !== selectedAreaId.current
      );

      if (isNameExists) {
        ToastService.error("Area name already exists");
        return;
      }

      const updatedAreas = localAreas.map((area) => {
        if (area.id === selectedAreaId.current) {
          return { ...area, name };
        }
        return area;
      });

      setLocalAreas(updatedAreas);
      setShowDialog(false);
      setIsAreasModified(true);
    }
  };

  const hasPermission = (users: IAssignedUser[]) => {
    return (
      !user?.roles.includes(SUBCONTRACTOR) ||
      (user?.roles.includes(SUBCONTRACTOR) &&
        users?.some((areaUser: any) => user?.id == areaUser?.userId))
    );
  };

  const handleAreaClick = (areaId: string) => {
    selectedAreaId.current = areaId;
    if (readOnlyMode) return;
    let area = localAreas.find((area) => area.id === areaId);

    // Check if this is a newly created area (will have ID starting with "new-area-")
    const isNewArea = areaId.startsWith("new-area-");

    if (!area || (isNewArea && !area.name)) {
      // For new areas without names, just show the dialog without any other actions
      setCurrentAreaName("");
      setShowDialog(true);
      return; // Important: Return early here!
    }
    if (isDeleteMode) {
      if (area?.status === ASSIGNED) {
        ToastService.error("You cannot delete an area with a request.");
        return;
      }
      setShowDeleteDialog(true);
      return;
    }

    if (
      (canEdit && area?.status === AVAILABLE) ||
      (isAreasModified && !area?.name && area?.status === AVAILABLE)
    ) {
      const clickedArea = localAreas.find((area) => area.id === areaId);
      setCurrentAreaName(clickedArea?.name || "");
      setShowDialog(true);
      return;
    }

    if (area?.status === RELEASED) {
      setShowAssignmentsDrawer(true);
    }

    if (canEdit && area?.status === ASSIGNED) {
      return;
    }

    if (
      currentLevel?.status === "Draft" ||
      (currentLevel?.status === "Published" && isAreasModified)
    ) {
      ToastService.error("Please publish the area to perform an action.");
      return;
    }

    if (area?.status === AVAILABLE && !user?.roles.includes(SUBCONTRACTOR)) {
      setShowAssignmentsDrawer(true);
      // setShowSubmissionDrawer(true);
      return;
    } else if (
      (area?.status === RELEASED || area?.status === ASSIGNED) &&
      hasPermission(area.assignedUsers)
    ) {
      setShowAssignmentsDrawer(true);
    }
  };

  // // When adding, deleting, or saving, always update main areas and filteredAreas
  // const handleAreasChange = (updatedAreas: IArea[]) => {
  //   setAreas((prevAreas) => {
  //     // 1) build a map of the changed shapes
  //     const updates = new Map(updatedAreas.map((a) => [a.id, a]));

  //     // 2) merge existing shapes (updating the ones in the map)
  //     const merged = prevAreas.map((a) =>
  //       updates.has(a.id) ? updates.get(a.id)! : a
  //     );

  //     // 3) find brand‑new shapes (e.g. your "new‑area‑12345")
  //     const newShapes = updatedAreas.filter(
  //       (a) => !prevAreas.some((p) => p.id === a.id)
  //     );

  //     // 4) return the full list: old ones (with updates) + any new ones
  //     return [...merged, ...newShapes];
  //   });

  //   setIsAreasModified(true);
  // };

  const [scale, setScale] = useState(1);
  const [fitScreen, setFitScreen] = useState(false);

  const zoomIn = () => {
    setScale((prevScale) => prevScale * 1.1);
  };

  const zoomOut = () => {
    setScale((prevScale) => prevScale / 1.1);
  };

  const fullScreen = () => {
    setFitScreen(true);
  };

  const userState = useAppSelector((state: RootState) => state.user);
  const anchor = React.useRef<HTMLButtonElement | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const navigateTo = useNavigate();

  React.useEffect(() => {
    setShow(false);
  }, []);

  const onClick = () => {
    setShow(!show);
  };

  const { instance } = useMsal();

  const logout = () => {
    const user = localStorage.getItem("user");

    if (user) {
      const userData = JSON.parse(user);
      if (userData?.idProvider === "EntraID") {
        instance.logoutRedirect();
      }
    }
    clearLocalStorageExcept([
      "selectedProjectId",
      "selectedLevel",
      "selectedUsersFilter",
      "selectedAreaStatus",
      "selectedAarStatus",
      "loginUser",
    ]);
    navigateTo("/");
  };

  const getInitials = (name: string) => {
    if (!name) return "";

    const words = name.split(" ");

    const initials = words.map((word) => word.charAt(0).toUpperCase()).join("");

    return initials.slice(0, 2);
  };

  const itemRender = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const index = itemProps.index;
    const itemChildren = (
      <div className="custom-dp-item">{li.props.children as any}</div>
    );

    return React.cloneElement(li, li.props, itemChildren);
  };

  const historyItemRender = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const index = itemProps.index;
    const itemChildren = (
      <div className="custom-dp-item">v{li.props.children as any}</div>
    );

    return React.cloneElement(li, li.props, itemChildren);
  };

  // This function controls how selected value renders in the input field
  const valueRender = (element: any) => {
    return (
      <div className="flex items-center w-full">
        v{currentLevelVersion?.version}
      </div>
    );
  };

  const handleDeleteDialog = (action: string) => {
    if (action === "yes") {
      const updatedAreas = localAreas.filter(
        (area) => area.id !== selectedAreaId.current
      );
      setLocalAreas(updatedAreas);
      setIsAreasModified(true);
    }

    setShowDeleteDialog(false);
  };

  const [error, setError] = useState<string | null>(null);

  // Inside the Home component, add these state variables after other state declarations
  const [filteredLevels, setFilteredLevels] = useState<any>([]);

  // Add this useEffect to initialize filtered levels when data changes
  useEffect(() => {
    if (levelsData?.data) {
      const filtered = filterLevels(levelsData.data);
      setFilteredLevels(filtered);
    }
  }, [levelsData?.data]);

  // Add these custom handlers for level ComboBox filtering and selection
  const handleLevelComboBoxChange = (e: any) => {
    // Call the original change handler to maintain existing functionality
    handleLevelChange(e.target.value?.id);

    // Reset filter after selection
    if (e.target.value) {
      // Reset to filtered list from original data
      const filtered = filterLevels(levelsData?.data || []);
      setFilteredLevels(filtered);
    }
  };

  const handleLevelComboBoxFilterChange = (e: any) => {
    const filter = e.filter;

    if (!filter) {
      // Reset to original filtered list when filter is cleared
      const filtered = filterLevels(levelsData?.data || []);
      setFilteredLevels(filtered);
    } else {
      // Filter levels based on filter text
      const allLevels = filterLevels(levelsData?.data || []);
      const filtered = allLevels.filter((level) =>
        level.name.toLowerCase().includes(filter?.value?.toLowerCase())
      );
      setFilteredLevels(filtered);
    }
  };

  // Utility to filter areas locally - All filters work in AND relation
  const filterAreasLocally = (
    areas: IArea[],
    subcon: string,
    areaStatus: string,
    aarStatus: string
  ) =>
    areas.filter((area) => {
      // Assigned-to-me filter
      if (
        assignedToMe &&
        !area.assignedUsers.some((u) => u.userId === user?.id)
      ) {
        return false;
      }

      // Subcontractor filter
      if (subcon && !area.assignedUsers.some((u) => u.userId === subcon)) {
        return false;
      }

      // Area Status filter
      if (areaStatus && area.status !== areaStatus) {
        return false;
      }

      // Company + Activity + AAR Status combined filtering using allRequests
      if (selectedCompany || selectedActivity || aarStatus) {
        // If we have allRequests, use it for more accurate filtering
        if (area.allRequests && Array.isArray(area.allRequests)) {
          // Filter requests that match all selected criteria
          const matchingRequests = area.allRequests.filter((request: any) => {
            // Check company filter
            if (selectedCompany) {
              const hasMatchingCompany = request.companies?.some(
                (c: any) => c.id === selectedCompany.id
              );
              if (!hasMatchingCompany) return false;
            }

            // Check activity filter (must be in the same company if company is selected)
            if (selectedActivity) {
              if (selectedCompany) {
                // Activity must be in the selected company's activities in this request
                const companyInRequest = request.companies?.find(
                  (c: any) => c.id === selectedCompany.id
                );
                if (companyInRequest) {
                  const hasMatchingActivity = companyInRequest.activities?.some(
                    (a: any) => a.name === selectedActivity.value
                  );
                  if (!hasMatchingActivity) return false;
                } else {
                  return false;
                }
              } else {
                // No company filter, just check if activity exists in any company
                const hasMatchingActivity = request.companies?.some((c: any) =>
                  c.activities?.some(
                    (a: any) => a.name === selectedActivity.value
                  )
                );
                if (!hasMatchingActivity) return false;
              }
            }

            // Check AAR status filter
            if (aarStatus && request.status !== aarStatus) {
              return false;
            }

            return true;
          });

          // If no requests match all the criteria, filter out this area
          if (matchingRequests.length === 0) {
            return false;
          }
        } else {
          // Fallback to basic filtering if allRequests is not available
          // Activities filter (filter by assignedActivities)
          if (selectedActivity && area.assignedActivities) {
            if (
              !area.assignedActivities.some(
                (a: string) => a === selectedActivity.value
              )
            ) {
              return false;
            }
          }

          // Company filter (filter by assignedCompanies)
          if (selectedCompany && area.assignedCompanies) {
            if (
              !area.assignedCompanies.some(
                (c: any) => c.companyId === selectedCompany.id
              )
            ) {
              return false;
            }
          }

          // AAR Status filter (basic flags)
          if (aarStatus) {
            if (aarStatus === ACCEPTED && !area.hasAcceptedRequest) {
              return false;
            }
            if (aarStatus === RELEASED && !area.hasReleasedRequest) {
              return false;
            }
            if (aarStatus === REJECTED && !area.hasRejectedRequest) {
              return false;
            }
            if (aarStatus === ASSIGNED && !area.hasAssignedRequest) {
              return false;
            }
          }
        }
      }

      return true;
    });

  // derive filteredAreas with useMemo as before
  const filteredAreas = useMemo(
    () =>
      filterAreasLocally(
        localAreas,
        selectedSubcon?.id || "",
        selectedAreaStatus || "",
        selectedAarStatus || ""
      ),
    [
      localAreas,
      selectedSubcon,
      selectedAreaStatus,
      selectedAarStatus,
      selectedActivity,
      assignedToMe,
    ]
  );

  // Add these state variables
  const [filteredCompanies, setFilteredCompanies] = useState<
    ICompanyWithUsers[]
  >([]);
  const [filteredSubcons, setFilteredSubcons] = useState<ICompanyUser[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<ILookup[]>([]);

  // Update filteredCompanies when companies data changes
  useEffect(() => {
    const sortedCompanies = (companies?.data || []).sort((a, b) =>
      a.name?.trim().localeCompare(b.name?.trim())
    );
    setFilteredCompanies(sortedCompanies);
  }, [companies?.data]);

  // Update filteredSubcons when assignedSubcon changes
  useEffect(() => {
    setFilteredSubcons(assignedSubcon || []);
  }, [assignedSubcon]);

  // Update filteredActivities when activitiesOptions changes
  useEffect(() => {
    console.log(
      "🔍 [Home] activitiesOptions updated - count:",
      activitiesOptions?.length
    );
    setFilteredActivities(activitiesOptions || []);
  }, [activitiesOptions]);

  // Filtering handlers
  const handleCompanyFilterChange = (e: any) => {
    const filterValue = e.filter?.value?.toLowerCase().trim() || "";
    if (!filterValue) {
      const sortedCompanies = (companies?.data || []).sort((a, b) =>
        a.name?.trim().localeCompare(b.name?.trim())
      );
      setFilteredCompanies(sortedCompanies);
    } else {
      const filtered = (companies?.data || [])
        .filter((c) => c.name?.trim().toLowerCase().includes(filterValue))
        .sort((a, b) => a.name?.trim().localeCompare(b.name?.trim()));
      setFilteredCompanies(filtered);
    }
  };

  const handleSubconFilterChange = (e: any) => {
    const filterValue = e.filter?.value?.toLowerCase() || "";
    if (!filterValue) {
      setFilteredSubcons(assignedSubcon || []);
    } else {
      setFilteredSubcons(
        (assignedSubcon || []).filter((u) =>
          u.name?.toLowerCase().includes(filterValue)
        )
      );
    }
  };

  const handleActivitiesFilterChange = (e: any) => {
    const filterValue = e.filter?.value?.toLowerCase() || "";
    if (!filterValue) {
      setFilteredActivities(activitiesOptions || []);
    } else {
      setFilteredActivities(
        (activitiesOptions || []).filter((a) =>
          a.value.toLowerCase().includes(filterValue)
        )
      );
    }
  };


  return (
    <>
      {(isLoadingLevels || isFetchingLevels || isLoadingAreas || isFetchingAreas) && (
        <div className="fixed inset-0 flex items-center justify-center bg-white/20 z-[100003]">
          <Player autoplay loop src={animationData} className="w-[80px]">
            <Controls
              visible={false}
              buttons={["play", "repeat", "frame", "debug"]}
            />
          </Player>
        </div>
      )}
      {showDialog && (
        <NameDialog
          setShowDialog={setShowDialog}
          onNameSave={handleSaveName}
          initialName={currentAreaName}
        />
      )}

      {showDeleteDialog && (
        <CustomDialogBox
          dialogInfo={{
            title: "Delete Area",
            message: "Are you sure you want to delete this area?",
          }}
          onToggleDeleteDialog={handleDeleteDialog}
        />
      )}

      <div className="h-screen bg-gray-800 pt-10">
        <div
          className="h-15 bg-[#000E39] fixed top-0 w-full z-[99999] flex items-center p-4 gap-2 topBar"
          id="topBar"
        >
          <img src="./images/alec_white_logo.png" alt="" width={100} />

          <span className="inline-flex h-[22px] w-[1px] bg-[#414148] mx-[22px]"></span>
          <ProjectDropdown />
          <div className="ml-auto flex items-center gap-x-1">
            {/* <div className="mr-[20px] p-3 border-r border-r-[#414148]"></div> */}
            <button
              onClick={() => {
                window.location.reload();
              }}
            >
              <RefreshCcw className="w-6 h-6 mr-2" color="white" />
            </button>
            <Alert color="white" />
            {isAuthorize([PROJECT_ADMIN, ENGINEER, SUBCONTRACTOR]) &&
              !isTouchScreen() && (
                <button
                  onClick={() => {
                    if (user?.roles.includes(SUPER_ADMIN)) {
                      navigate("/settings/projects");
                    } else if (user?.roles.includes(ENGINEER) || user?.roles.includes(SUBCONTRACTOR)) {
                      navigate("/settings/reports");
                    } else {
                      navigate("/settings/levels");
                    }
                  }}
                >
                  <Settings className="w-6 h-6 ml-2" color="white" />
                </button>
              )}

            <div className="mr-[20px] p-3 border-r border-r-[#414148]"></div>
            <button
              className={`${
                user?.roles.includes(SUBCONTRACTOR)
                  ? "" //
                  : "outline-none border-none"
              } p-0 inline-flex items-center gap-[10px] hover:bg-transparent bg-transparent`}
              onClick={togglePopup}
              ref={anchor}
            >
              <span className="text-sm font-normal sm:block hidden text-white">
                {userState?.user?.displayName}
              </span>
              <Avatar
                rounded="full"
                type="text"
                style={{ backgroundColor: "#a4262c" }}
              >
                {userState?.user?.displayName
                  ? getInitials(userState?.user?.displayName)
                  : ""}
              </Avatar>
            </button>

            <Popup
              anchor={anchor.current}
              show={show}
              anchorAlign={{ horizontal: "right", vertical: "bottom" }}
              popupAlign={{ horizontal: "right", vertical: "top" }}
              margin={{ horizontal: 0, vertical: 9 }}
              popupClass={"menuPopup viewerMenuPopup"}
            >
              {/* Add ref to the popup content */}
              <div ref={popupRef}>
                <div className="p-2">
                  <p className="block line-clamp-1 break-words text-xs">
                    Signed in as
                  </p>
                  <span className="block line-clamp-1 break-words text-xs">
                    {userState?.user?.principalName}
                  </span>
                  <span
                    className="block line-clamp-1 break-words text-xs"
                    style={{ color: "#918b8b" }}
                  ></span>
                  <span
                    className="block line-clamp-1 break-words text-xs text-white"
                    style={{ color: "#918b8b" }}
                  >
                    {userState?.user?.roles?.join(", ")}
                  </span>
                </div>
                <ul>
                  <li className="border-t border-[#555555]">
                    <Link
                      to="/change-password"
                      className="inline-flex items-center py-2 px-2 gap-2 text-white hover:bg-blue-600 w-full transition-all duration-300"
                    >
                      <AsteriskSquare className="w-4 h-4" /> Change password
                    </Link>
                  </li>
                  <li className="border-t border-[#555555]">
                    <span
                      className="inline-flex items-center py-2 px-2 gap-2 text-white hover:bg-blue-600 w-full transition-all duration-300 cursor-pointer"
                      onClick={logout}
                    >
                      <LogOut className="w-4 h-4" /> Sign out
                    </span>
                  </li>
                </ul>
              </div>
            </Popup>
          </div>
        </div>
        <div className="h-12 fixed top-18 w-full z-50 flex items-center p-4 gap-2">
          {levelsData?.data && levelsData?.data?.length > 0 && (
            <>
              <ComboBox
                rounded={"medium"}
                data={filteredLevels}
                size={"small"}
                name="levelId"
                itemRender={itemRender}
                textField="name"
                dataItemKey="id"
                onChange={handleLevelComboBoxChange}
                onFilterChange={handleLevelComboBoxFilterChange}
                style={{ width: "250px" }}
                value={currentLevel}
                filterable={true}
                clearButton={false}
                className="viewer-dp border-black bg-[#212123] shadow-[8px_8px_8.4px_0px_rgba(0,0,0,0.44)]"
              />
              {isAuthorize([SUPER_ADMIN, PROJECT_ADMIN, ENGINEER]) && (
                <>
                  <ComboBox
                    rounded={"medium"}
                    size={"small"}
                    itemRender={itemRender}
                    data={filteredCompanies}
                    placeholder="Select Company"
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.value)}
                    dataItemKey="id"
                    textField="name"
                    style={{ width: "250px" }}
                    className="viewer-dp border-black bg-[#212123] shadow-[8px_8px_8.4px_0px_rgba(0,0,0,0.44)]"
                    filterable={true}
                    onFilterChange={handleCompanyFilterChange}
                  />
                  <ComboBox
                    rounded={"medium"}
                    size={"small"}
                    itemRender={itemRender}
                    data={filteredSubcons}
                    placeholder="Select Subcontractor"
                    value={selectedSubcon}
                    onChange={userFilterChange}
                    dataItemKey="id"
                    textField="name"
                    style={{ width: "300px" }}
                    className="viewer-dp border-black bg-[#212123] shadow-[8px_8px_8.4px_0px_rgba(0,0,0,0.44)]"
                    filterable={true}
                    onFilterChange={handleSubconFilterChange}
                  />
                  <ComboBox
                    rounded={"medium"}
                    size={"small"}
                    itemRender={itemRender}
                    data={filteredActivities}
                    placeholder="Select Activity"
                    value={selectedActivity}
                    onChange={(e) => setSelectedActivity(e.value)}
                    dataItemKey="id"
                    textField="value"
                    style={{ width: "250px" }}
                    className="viewer-dp border-black bg-[#212123] shadow-[8px_8px_8.4px_0px_rgba(0,0,0,0.44)]"
                    filterable={true}
                    onFilterChange={handleActivitiesFilterChange}
                  />
                </>
              )}
              {/* Area Status dropdown */}
              <ComboBox
                rounded={"medium"}
                size={"small"}
                data={[
                  { label: "Available Area", value: AVAILABLE },
                  { label: "Assigned Area", value: ASSIGNED },
                  { label: "Released Area", value: RELEASED },
                ]}
                textField="label"
                dataItemKey="value"
                itemRender={itemRender}
                placeholder="Select Area Status"
                name="areaStatus"
                onChange={areaStatusChange}
                style={{ width: "200px" }}
                className="viewer-dp border-black bg-[#212123] shadow-[8px_8px_8.4px_0px_rgba(0,0,0,0.44)]"
                filterable={true}
              />
              {/* AAR Status dropdown */}
              <ComboBox
                rounded={"medium"}
                size={"small"}
                data={[
                  { label: "Accepted AAR", value: ACCEPTED },
                  { label: "Assigned AAR", value: ASSIGNED },
                  { label: "Rejected AAR", value: REJECTED },
                  { label: "Released AAR", value: RELEASED },
                  { label: "Released - Pending", value: RELEASE_PENDING },
                  { label: "Overdue AAR", value: OVERDUE },
                ]}
                textField="label"
                dataItemKey="value"
                itemRender={itemRender}
                placeholder="Select AAR Status"
                name="aarStatus"
                onChange={aarStatusChange}
                style={{ width: "200px" }}
                className="viewer-dp border-black bg-[#212123] shadow-[8px_8px_8.4px_0px_rgba(0,0,0,0.44)]"
                filterable={true}
              />
              {/* Assigned-to-me dropdown */}
              <ComboBox
                rounded={"medium"}
                size={"small"}
                data={[
                  { label: "All", value: false },
                  { label: "Assigned to me", value: true },
                ]}
                textField="label"
                itemRender={itemRender}
                dataItemKey="value"
                value={
                  assignedToMe
                    ? { label: "Assigned to me", value: true }
                    : { label: "All", value: false }
                }
                onChange={(e) => setAssignedToMe(e.value?.value)}
                style={{ width: "175px" }}
                className="viewer-dp border-black bg-[#212123] shadow-[8px_8px_8.4px_0px_rgba(0,0,0,0.44)]"
              />
            </>
          )}
          <div className="ml-auto flex items-center">
            {levelHistory &&
              levelHistory?.length > 0 &&
              (currentLevel?.status !== "Draft" || canEdit) &&
              isAuthorize([PROJECT_ADMIN]) && (
                <ComboBox
                  rounded={"medium"}
                  data={levelHistory}
                  size={"small"}
                  className="viewer-dp border-black bg-[#212123] shadow-[8px_8px_8.4px_0px_rgba(0,0,0,0.44)]"
                  name="levelHistoryId"
                  itemRender={historyItemRender}
                  textField="version"
                  dataItemKey="id"
                  valueRender={valueRender}
                  onChange={(e) => handleLevelHistoryChange(e.target?.value)}
                  style={{ width: "65px" }}
                  value={
                    levelHistory?.find((x) => {
                      return (
                        x.id === currentLevelVersion?.id &&
                        x.version === currentLevelVersion?.version
                      );
                    }) || null
                  }
                  clearButton={false}
                  filterable={true}
                />
              )}
            {isAreasModified && (
              <>
                <button
                  onClick={() => handleSaveLevel("draft")}
                  className="p-2 px-2 rounded bg-blue-600 text-white flex items-center mr-2 ml-2"
                  disabled={updateLevelMutation.isPending}
                >
                  <Save className="w-5 h-5 mr-1" />
                  {updateLevelMutation.isPending ? "Saving..." : "Draft"}
                </button>

                {currentLevel?.status !== "Draft" && (
                  <button
                    onClick={() => handleSaveLevel("publish")}
                    className="p-2 px-2 rounded bg-green-600 text-white flex items-center mr-2"
                    disabled={updateLevelMutation.isPending}
                  >
                    <BookCheck className="w-5 h-5 mr-1" />
                    {updateLevelMutation.isPending ? "Saving..." : "Publish"}
                  </button>
                )}
              </>
            )}
            {currentLevel?.status === "Draft" && canEdit && (
              <button
                onClick={() => handleSaveLevel("publish")}
                className="p-2 px-2 rounded bg-green-600 text-white flex items-center ml-2"
                disabled={updateLevelMutation.isPending}
              >
                <Save className="w-5 h-5 mr-1" />
                {updateLevelMutation.isPending ? "Saving..." : "Publish"}
              </button>
            )}
            {currentLevel?.status === "Draft" && !canEdit && (
              <span className="text-white bg-primary font-bold px-3 py-[5px]">
                Draft
              </span>
            )}
          </div>
        </div>
        <CanvasEditor
          imageUrl={imageUrl}
          areas={filteredAreas}
          onAreaClick={handleAreaClick}
          onAreasChange={setLocalAreas}
          isDrawMode={isDrawMode}
          scale={scale}
          setScale={setScale}
          fitScreen={fitScreen}
          setFitScreen={setFitScreen}
          drawLabels={drawLabels}
          shapeType={shapeType}
          selectedCompanyId={selectedCompany?.id ?? null}
          selectedAarStatus={selectedAarStatus || null}
          selectedActivityValue={selectedActivity?.value ?? null}
        />
        <div className="sidebar">
          <button
            id="fitToWidthBtn"
            title="Fit To Screen"
            onClick={() => {
              fullScreen();
            }}
          >
            <img src="/images/sidebar/fullScreen.png" alt="" />
          </button>
          <button
            id="zoomInBtn"
            title="Zoom In"
            onClick={() => {
              zoomIn();
            }}
          >
            <img src="/images/sidebar/zoomIn.png" alt="" />
          </button>
          <button
            id="zoomOutBtn"
            title="Zoom Out"
            onClick={() => {
              zoomOut();
            }}
          >
            <img src="/images/sidebar/zoomOut.png" alt="" />
          </button>
          <button
            id="toggleEditBtn"
            title="Show Labels"
            onClick={() => {
              setDrawLabels(!drawLabels);
            }}
          >
            <img src="/images/sidebar/list.png" alt="" />
          </button>

          {isAuthorize([PROJECT_ADMIN]) &&
            !readOnlyMode &&
            !isTouchScreen() && (
              <ToggleButton
                setCanEdit={setCanEdit}
                setIsDrawMode={setIsDrawMode}
                setShapeType={setShapeType}
              />
            )}

          {canEdit && isAuthorize([PROJECT_ADMIN]) && (
            <>
              {/* <button
                onClick={() => {
                  setIsDrawMode(!isDrawMode);
                  setIsDeleteMode(false);
                }}
                className={`p-2 rounded ${
                  isDrawMode ? "bg-blue-600" : ""
                } transition-colors`}
              >
                <PenTool className="w-5 h-5" color="white" />
              </button> */}
              {/* button for square shape */}
              <button
                onClick={() => {
                  if (isDrawMode && shapeType != "square") setIsDrawMode(true);
                  else setIsDrawMode(!isDrawMode);
                  setIsDeleteMode(false);
                  setShapeType("square");
                }}
                className={`p-2 rounded ${
                  isDrawMode && shapeType === "square" ? "bg-blue-600" : ""
                } transition-colors`}
              >
                <Square className="w-5 h-5" color="white" />
              </button>
              {/* button for hexagon shape */}
              <button
                onClick={() => {
                  if (isDrawMode && shapeType != "hexagon") setIsDrawMode(true);
                  else setIsDrawMode(!isDrawMode);
                  setIsDeleteMode(false);
                  setShapeType("hexagon");
                }}
                className={`p-2 rounded ${
                  isDrawMode && shapeType === "hexagon" ? "bg-blue-600" : ""
                } transition-colors`}
              >
                <Hexagon className="w-5 h-5" color="white" />
              </button>
              {/* button for Octagon shape */}
              <button
                onClick={() => {
                  if (isDrawMode && shapeType !== "octagon")
                    setIsDrawMode(true);
                  else setIsDrawMode(!isDrawMode);
                  setIsDeleteMode(false);
                  setShapeType("octagon");
                }}
                className={`p-2 rounded ${
                  isDrawMode && shapeType === "octagon" ? "bg-blue-600" : ""
                } transition-colors`}
              >
                <Octagon className="w-5 h-5" color="white" />
              </button>
              {/* button for Octagon shape */}
              <button
                onClick={() => {
                  if (isDrawMode && shapeType !== "decagon")
                    setIsDrawMode(true);
                  else setIsDrawMode(!isDrawMode);
                  setIsDeleteMode(false);
                  setShapeType("decagon");
                }}
                className={`p-2 rounded ${
                  isDrawMode && shapeType === "decagon" ? "bg-blue-600" : ""
                } transition-colors`}
              >
                <Badge className="w-5 h-5" color="white" />
              </button>
              <button
                id="deleteBtn"
                title="Enable Edit"
                onClick={() => {
                  setIsDeleteMode(!isDeleteMode);
                  setIsDrawMode(false);
                }}
                className={`p-2 rounded ${
                  isDeleteMode ? "bg-blue-600" : ""
                } transition-colors`}
              >
                {/* <img src="/images/sidebar/delete.png" alt="" /> */}

                <Trash2 className="w-5 h-5" color="white" />
              </button>
            </>
          )}
        </div>
      </div>
      <SubmissionDrawer
        readOnlyMode={readOnlyMode}
        companies={companies}
        showSubmissionDrawer={showSubmissionDrawer}
        setShowSubmissionDrawer={setShowSubmissionDrawer}
        setShowDetailsDrawer={setShowDetailsDrawer}
        selectedArea={localAreas.find(
          (area) => area.id === selectedAreaId.current
        )}
        selectedLevel={currentLevel}
        setError={setError}
        error={error}
      />
      <AssignmentsDrawer
        setIsViewMode={setIsViewMode}
        handlePreview={handlePreview}
        readOnlyMode={readOnlyMode}
        handleAction={handleAction}
        showAssignmentsDrawer={showAssignmentsDrawer}
        setShowAssignmentsDrawer={setShowAssignmentsDrawer}
        setShowDetailsDrawer={setShowDetailsDrawer}
        showDetailsDrawer={showDetailsDrawer}
        showSubmissionDrawer={showSubmissionDrawer}
        setShowSubmissionDrawer={setShowSubmissionDrawer}
        selectedArea={localAreas.find(
          (area) => area.id === selectedAreaId.current
        )}
        selectedRequest={selectedRequest}
        setSelectedRequest={setSelectedRequest}
        selectedLevel={currentLevel}
        imagePreviewWidth={imagePreviewWidth}
        imagePreviewHeight={imagePreviewHeight}
      />
      <DetailsDrawer
        setIsViewMode={setIsViewMode}
        handlePreview={handlePreview}
        readOnlyMode={readOnlyMode}
        handleAction={handleAction}
        showDetailsDrawer={showDetailsDrawer}
        setShowDetailsDrawer={setShowDetailsDrawer}
        showAssignmentsDrawer={showAssignmentsDrawer}
        setShowAssignmentsDrawer={setShowAssignmentsDrawer}
        showSubmissionDrawer={showSubmissionDrawer}
        setShowSubmissionDrawer={setShowSubmissionDrawer}
        selectedArea={localAreas.find(
          (area) => area.id === selectedAreaId.current
        )}
        selectedRequest={selectedRequest}
        selectedLevel={currentLevel}
        imagePreviewWidth={imagePreviewWidth}
        imagePreviewHeight={imagePreviewHeight}
      />
      <TaskActionDrawer
        isViewMode={isViewMode}
        setIsViewMode={setIsViewMode}
        handlePreview={handlePreview}
        readOnlyMode={readOnlyMode}
        setShowTaskActionDrawer={setShowTaskActionDrawer}
        showTaskActionDrawer={showTaskActionDrawer}
        taskAction={taskAction}
        setTaskAction={setTaskAction}
        selectedArea={localAreas.find(
          (area) => area.id === selectedAreaId.current
        )}
        selectedRequest={selectedRequest}
        selectedLevelId={currentLevel?.id}
        setShowDetailsDrawer={setShowDetailsDrawer}
      />

      {showImageDialog && (
        <Dialog
          width={imagePreviewWidth}
          height={"80vh"}
          title={"Preview"}
          onClose={() => setShowImageDialog(false)}
          className="image-dialog overflow-hidden"
        >
          <div className="flex justify-center items-center h-full max-h-[80vh]">
            <img src={imageSrc} className="w-full h-full"></img>
          </div>
        </Dialog>
      )}
    </>
  );
}

export default Home;
